import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Eye, Database, Envelope, Phone, MapPin } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

const PrivacyPolicyPage = () => {
  const { t } = useTranslation();

  return (
    <div className="bg-white py-16 sm:py-24">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-4xl mx-auto"
        >
          {/* Header */}
          <div className="text-center mb-16">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-[#0D2B45] rounded-full flex items-center justify-center">
                <Shield size={32} className="ph-thin text-white" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold font-heading mb-4">
              {t('privacyPolicy.title')}
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('privacyPolicy.subtitle')}
            </p>
            <p className="text-sm text-gray-500 mt-4">
              {t('privacyPolicy.lastUpdated')}: {t('privacyPolicy.date')}
            </p>
          </div>

          {/* Contact Information */}
          <div className="bg-gray-50 p-8 rounded-lg mb-12">
            <h2 className="text-2xl font-bold font-heading mb-6 flex items-center gap-3">
              <Envelope size={24} className="ph-thin text-white" />
              {t('privacyPolicy.contact.title')}
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">{t('privacyPolicy.contact.company')}</h3>
                <p className="text-gray-600">Juliux</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <Envelope size={16} className="ph-thin text-[#0D2B45]" />
                  {t('privacyPolicy.contact.email')}
                </h3>
                <a href="mailto:info@juliux.nl" className="text-[#0D2B45] hover:underline">
                  info@juliux.nl
                </a>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <MapPin size={16} className="ph-thin text-[#0D2B45]" />
                  {t('privacyPolicy.contact.address')}
                </h3>
                <p className="text-gray-600">{t('privacyPolicy.contact.addressValue')}</p>
              </div>
            </div>
          </div>

          {/* Privacy Policy Content */}
          <div className="prose prose-lg max-w-none">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="mb-12"
            >
              <h2 className="text-2xl font-bold font-heading mb-6 flex items-center gap-3">
                <Eye size={24} className="ph-thin text-[#0D2B45]" />
                {t('privacyPolicy.sections.overview.title')}
              </h2>
              <div className="text-gray-700 leading-relaxed space-y-4">
                <p>{t('privacyPolicy.sections.overview.content1')}</p>
                <p>{t('privacyPolicy.sections.overview.content2')}</p>
                <p>{t('privacyPolicy.sections.overview.content3')}</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="mb-12"
            >
              <h2 className="text-2xl font-bold font-heading mb-6 flex items-center gap-3">
                <Database size={24} className="ph-thin text-[#0D2B45]" />
                {t('privacyPolicy.sections.dataCollection.title')}
              </h2>
              <div className="text-gray-700 leading-relaxed space-y-4">
                <p>{t('privacyPolicy.sections.dataCollection.content1')}</p>
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                  {t('privacyPolicy.sections.dataCollection.personalData.title')}
                </h3>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>{t('privacyPolicy.sections.dataCollection.personalData.item1')}</li>
                  <li>{t('privacyPolicy.sections.dataCollection.personalData.item2')}</li>
                  <li>{t('privacyPolicy.sections.dataCollection.personalData.item3')}</li>
                  <li>{t('privacyPolicy.sections.dataCollection.personalData.item4')}</li>
                </ul>
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                  {t('privacyPolicy.sections.dataCollection.automaticData.title')}
                </h3>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>{t('privacyPolicy.sections.dataCollection.automaticData.item1')}</li>
                  <li>{t('privacyPolicy.sections.dataCollection.automaticData.item2')}</li>
                  <li>{t('privacyPolicy.sections.dataCollection.automaticData.item3')}</li>
                  <li>{t('privacyPolicy.sections.dataCollection.automaticData.item4')}</li>
                </ul>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="mb-12"
            >
              <h2 className="text-2xl font-bold font-heading mb-6">
                {t('privacyPolicy.sections.dataUsage.title')}
              </h2>
              <div className="text-gray-700 leading-relaxed space-y-4">
                <p>{t('privacyPolicy.sections.dataUsage.content1')}</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>{t('privacyPolicy.sections.dataUsage.item1')}</li>
                  <li>{t('privacyPolicy.sections.dataUsage.item2')}</li>
                  <li>{t('privacyPolicy.sections.dataUsage.item3')}</li>
                  <li>{t('privacyPolicy.sections.dataUsage.item4')}</li>
                  <li>{t('privacyPolicy.sections.dataUsage.item5')}</li>
                </ul>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="mb-12"
            >
              <h2 className="text-2xl font-bold font-heading mb-6">
                {t('privacyPolicy.sections.cookies.title')}
              </h2>
              <div className="text-gray-700 leading-relaxed space-y-4">
                <p>{t('privacyPolicy.sections.cookies.content1')}</p>
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                  {t('privacyPolicy.sections.cookies.types.title')}
                </h3>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>{t('privacyPolicy.sections.cookies.types.necessary')}</strong> - {t('privacyPolicy.sections.cookies.types.necessaryDesc')}</li>
                  <li><strong>{t('privacyPolicy.sections.cookies.types.analytics')}</strong> - {t('privacyPolicy.sections.cookies.types.analyticsDesc')}</li>
                  <li><strong>{t('privacyPolicy.sections.cookies.types.marketing')}</strong> - {t('privacyPolicy.sections.cookies.types.marketingDesc')}</li>
                  <li><strong>{t('privacyPolicy.sections.cookies.types.preferences')}</strong> - {t('privacyPolicy.sections.cookies.types.preferencesDesc')}</li>
                </ul>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="mb-12"
            >
              <h2 className="text-2xl font-bold font-heading mb-6">
                {t('privacyPolicy.sections.dataSharing.title')}
              </h2>
              <div className="text-gray-700 leading-relaxed space-y-4">
                <p>{t('privacyPolicy.sections.dataSharing.content1')}</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>{t('privacyPolicy.sections.dataSharing.item1')}</li>
                  <li>{t('privacyPolicy.sections.dataSharing.item2')}</li>
                  <li>{t('privacyPolicy.sections.dataSharing.item3')}</li>
                </ul>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="mb-12"
            >
              <h2 className="text-2xl font-bold font-heading mb-6">
                {t('privacyPolicy.sections.dataSecurity.title')}
              </h2>
              <div className="text-gray-700 leading-relaxed space-y-4">
                <p>{t('privacyPolicy.sections.dataSecurity.content1')}</p>
                <p>{t('privacyPolicy.sections.dataSecurity.content2')}</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.7 }}
              className="mb-12"
            >
              <h2 className="text-2xl font-bold font-heading mb-6">
                {t('privacyPolicy.sections.yourRights.title')}
              </h2>
              <div className="text-gray-700 leading-relaxed space-y-4">
                <p>{t('privacyPolicy.sections.yourRights.content1')}</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>{t('privacyPolicy.sections.yourRights.item1')}</li>
                  <li>{t('privacyPolicy.sections.yourRights.item2')}</li>
                  <li>{t('privacyPolicy.sections.yourRights.item3')}</li>
                  <li>{t('privacyPolicy.sections.yourRights.item4')}</li>
                  <li>{t('privacyPolicy.sections.yourRights.item5')}</li>
                </ul>
                <p className="mt-4">{t('privacyPolicy.sections.yourRights.content2')}</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.8 }}
              className="mb-12"
            >
              <h2 className="text-2xl font-bold font-heading mb-6">
                {t('privacyPolicy.sections.changes.title')}
              </h2>
              <div className="text-gray-700 leading-relaxed space-y-4">
                <p>{t('privacyPolicy.sections.changes.content1')}</p>
                <p>{t('privacyPolicy.sections.changes.content2')}</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.9 }}
              className="bg-[#0D2B45] text-white p-8 rounded-lg"
            >
              <h2 className="text-2xl font-bold font-heading mb-4 text-white">
                {t('privacyPolicy.contact.title')}
              </h2>
              <p className="text-white/90 mb-4">
                {t('privacyPolicy.contact.description')}
              </p>
              <a
                href="mailto:info@juliux.nl"
                className="inline-flex items-center px-6 py-3 bg-white text-[#0D2B45] font-medium rounded-lg hover:bg-gray-100 transition-colors"
              >
                <Envelope size={20} className="ph-thin mr-2" />
                {t('privacyPolicy.contact.button')}
              </a>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
